import { createApp, nextTick } from 'vue'
import { createPinia } from 'pinia'
import * as Sentry from '@sentry/vue'
import Toast from 'vue-toastification'
import 'vue-toastification/dist/index.css'
import App from './App.vue'
import router from './router'
import {
  cleanupChunkRecoveryQueryParamFromAddressBar,
  clearChunkRecoveryAttemptState,
  consumeChunkRecoveryTarget,
  showChunkRecoveryPendingToastIfAny,
} from '@/utils/chunkLoadRecovery'
import 'remixicon/fonts/remixicon.css'
import './style.css'
import './styles/pmWeeklyCheckInCursor.css'

const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
app.use(router)
app.use(Toast, {
  position: 'top-right',
  timeout: 1500,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: false,
  closeButton: 'button',
  icon: true,
  rtl: false
})

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
    ],
    tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.1'),
    tracePropagationTargets: [/\/api\//],
  })
}

// Auth store is automatically initialized when created

// Global error handlers — also forward to Sentry alongside the backend errorLogger
app.config.errorHandler = (err, instance, info) => {
  const capture = (Sentry as { captureException?: (e: unknown, ctx?: object) => void }).captureException
  if (capture) capture(err, { extra: { info } })

  import('./services/errorLogger').then(({ default: errorLogger }) => {
    const route = router.currentRoute.value.path
    const component = instance?.$options?.name || instance?.$options?.__name || 'Unknown'

    errorLogger.logError(
      'ui',
      'component_error',
      route,
      err instanceof Error ? err : new Error(String(err)),
      component
    )
  })
  
  console.error('Vue error:', err, info)
}

// Global JavaScript error handler
window.onerror = (message, source, lineno, colno, error) => {
  import('./services/errorLogger').then(({ default: errorLogger }) => {
    const route = router.currentRoute.value.path
    
    errorLogger.logError(
      'ui',
      'javascript_error',
      route,
      error || new Error(String(message)),
      undefined,
      {
        source,
        lineno: lineno?.toString(),
        colno: colno?.toString(),
      }
    )
  })
  
  // Return false to allow default error handling
  return false
}

// Global unhandled promise rejection handler
window.onunhandledrejection = (event) => {
  import('./services/errorLogger').then(({ default: errorLogger }) => {
    const route = router.currentRoute.value.path
    
    errorLogger.logError(
      'ui',
      'unhandled_promise_rejection',
      route,
      event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
      undefined
    )
  })
  
  // Prevent default browser error handling
  event.preventDefault()
}

void (async () => {
  try {
    await router.isReady()
    const recovered = consumeChunkRecoveryTarget()
    if (recovered) {
      if (recovered !== router.currentRoute.value.fullPath) {
        try {
          await router.replace(recovered)
          clearChunkRecoveryAttemptState()
        } catch {
          /* invalid route — let router / 404 handle; leave attempts for further errors */
        }
      } else {
        clearChunkRecoveryAttemptState()
      }
    } else {
      clearChunkRecoveryAttemptState()
    }
    cleanupChunkRecoveryQueryParamFromAddressBar()
  } catch (e) {
    console.error('Bootstrap navigation failed:', e)
  }
  app.mount('#app')
  await nextTick()
  showChunkRecoveryPendingToastIfAny()
})()
